import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthModal({ open, onOpenChange, defaultTab = "login" }) {
  const [tab, setTab] = useState(defaultTab);
  const { login, register } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  React.useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Signed in");
      } else {
        if (!form.name || !form.phone) { toast.error("Name and phone are required"); setSubmitting(false); return; }
        await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
        toast.success("Account created — welcome!");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Auth failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-2 border-gold rounded-none max-w-md p-0" data-testid="auth-modal">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-cream uppercase tracking-tight text-3xl">
              {tab === "login" ? "Sign In" : "Create Account"}
            </DialogTitle>
            <DialogDescription className="text-cream/70 font-body">
              Earn loyalty rewards · Track orders · Save your details.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex gap-1 border border-border w-fit">
            <button
              onClick={() => setTab("login")}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-sm ${tab === "login" ? "bg-gold text-black" : "text-cream/70 hover:text-cream"}`}
              data-testid="auth-tab-login"
            >Sign In</button>
            <button
              onClick={() => setTab("register")}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-sm ${tab === "register" ? "bg-gold text-black" : "text-cream/70 hover:text-cream"}`}
              data-testid="auth-tab-register"
            >Register</button>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            {tab === "register" && (
              <>
                <Field label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="auth-name" />
                <Field label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} testid="auth-phone" />
              </>
            )}
            <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="auth-email" />
            <Field label="Password *" type="password" value={form.password} onChange={(v) => set("password", v)} testid="auth-password" />
            <button type="submit" disabled={submitting} className="w-full btn-poster mt-2" data-testid="auth-submit">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
              {tab === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-cream/70">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream"
        data-testid={testid}
        required
      />
    </div>
  );
}
